import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { friendlyMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { authApi } from '@/lib/api/services/auth';
import { useAuth } from '@/components/AuthProvider';
import { validatePhoneForCountry } from '@/lib/constants/phoneValidation';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { OtpInput } from '@/components/OtpInput';

interface PhoneOtpLoginFormProps {
  /** Called when the account has 2FA enabled and a TOTP code is still required. */
  onRequiresTotp: (userId: string) => void;
  /** Called after a fully successful login (tokens already stored). */
  onSuccess: () => void;
  /** Reports which step is showing (and the display phone once on OTP) so the
   *  parent can render a back arrow + the "code sent to <phone>" subtitle. */
  onStepChange?: (isOtpStep: boolean, phoneNumberDisplay?: string) => void;
}

/** Imperative handle so the parent's back arrow can return to the phone step. */
export interface PhoneOtpLoginFormHandle {
  back: () => void;
}

/**
 * Phone + WhatsApp OTP login. Reuses the same country-code picker + phone
 * validation as signup. Two steps: enter phone → receive OTP → verify & sign in.
 * (Password login lives in the email/phone + password form — see Auth.tsx.)
 */
export const PhoneOtpLoginForm = forwardRef<PhoneOtpLoginFormHandle, PhoneOtpLoginFormProps>(
  function PhoneOtpLoginForm({ onRequiresTotp, onSuccess, onStepChange }, ref) {
  const { loginWithOtp } = useAuth();
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  // Field-level validation errors, rendered directly under each input.
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');

  // E.164 for the API (no space); a separate spaced form for display only.
  const phoneNumber = `${countryCode}${phoneDisplay.replace(/\D/g, '')}`;
  const phoneNumberDisplay = `${countryCode} ${phoneDisplay.replace(/\D/g, '')}`;

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleSendOtp = async () => {
    // Validate on click (button is always enabled) — show the error under the field.
    const pErr = validatePhoneForCountry(countryCode, phoneDisplay);
    if (pErr) {
      setPhoneError(pErr);
      return;
    }
    setPhoneError('');
    setSending(true);
    try {
      const result = await authApi.sendOTP(phoneNumber, undefined, undefined, 'login');
      if (result.data) {
        setOtpSent(true);
        setCountdown(60);
        toast({ variant: "success", title: 'OTP Sent!', description: 'Check your WhatsApp for the verification code.' });
      } else {
        setPhoneError(result.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      setPhoneError(friendlyMessage(err, 'Failed to send OTP'));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit code sent to your WhatsApp');
      return;
    }
    setOtpError('');
    setVerifying(true);
    try {
      const result = await loginWithOtp(phoneNumber, otpCode.trim());
      if (result.success) {
        if (result.requires_totp && result.userId) {
          onRequiresTotp(result.userId);
          return;
        }
        onSuccess();
      } else {
        setOtpError(result.error || 'Login failed');
      }
    } catch (err: any) {
      setOtpError(friendlyMessage(err, 'Login failed'));
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit once all 6 digits are entered — the segmented input has no
  // explicit verify button. Guarded so it fires once per completed code and
  // never while a verification is already in flight (so it can't loop on error).
  useEffect(() => {
    if (otpSent && otpCode.length === 6 && !verifying) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, otpSent]);

  // Expose a back() so the parent header arrow can return to the phone step.
  useImperativeHandle(ref, () => ({
    back: () => {
      setOtpSent(false);
      setOtpCode('');
      setOtpError('');
      setCountdown(0);
    },
  }), []);

  // Report the active step so the parent can show/hide the header back arrow.
  useEffect(() => {
    onStepChange?.(otpSent, phoneNumberDisplay);
  }, [otpSent, phoneNumberDisplay, onStepChange]);

  return (
    <div className="space-y-4">
      {!otpSent ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="login-phone">Phone number</Label>
            <PhoneNumberInput
              id="login-phone"
              className="h-12"
              countryCode={countryCode}
              phoneDisplay={phoneDisplay}
              invalid={!!phoneError}
              onCountryChange={(code) => {
                setCountryCode(code);
                if (phoneError) setPhoneError('');
              }}
              onPhoneChange={(digits) => {
                setPhoneDisplay(digits);
                if (phoneError) setPhoneError('');
              }}
            />
            {phoneError && <p className="text-xs text-red-600">{phoneError}</p>}
          </div>
          <Button type="button" className="w-full !h-12 font-bold" onClick={handleSendOtp} disabled={sending}>
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send OTP
          </Button>
        </>
      ) : (
        <>
          <OtpInput
            value={otpCode}
            onChange={(v) => {
              setOtpCode(v);
              if (otpError) setOtpError('');
            }}
            invalid={!!otpError}
            disabled={verifying}
            autoFocus
          />

          {otpError && <p className="text-xs text-red-600">{otpError}</p>}
          {verifying && (
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
            </p>
          )}

          <div className="flex items-center justify-end pt-1">
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={countdown > 0 || sending}
              className="text-sm font-semibold text-[var(--secondary-color)] hover:underline disabled:no-underline disabled:opacity-60"
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>
          </div>
        </>
      )}
    </div>
  );
  },
);
